import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi, useDirectory } from "@/hooks/useApi";
import { PageHeader, FilterBar, ErrorBox, Loading } from "@/components/common/ui-bits";
import { FormDialog } from "@/components/common/FormDialog";
import { KanbanView, ListView, CalendarView, TimelineView } from "@/components/tasks/TaskViews";
import { TaskSheet } from "@/components/tasks/TaskSheet";
import { taskFields } from "@/lib/schemas";
import { optionList, PRIORITIES } from "@/lib/format";

export default function Tasks() {
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("kanban");
  const [q, setQ] = useState("");
  const [f, setF] = useState({});
  const { data, loading, error, reload, setData } = useApi("/tasks", { q, ...f });
  const { data: projects } = useApi("/projects");
  const { options: staff, name } = useDirectory();
  const [creating, setCreating] = useState(false);
  const projectOptions = (projects || []).map((p) => ({ value: p.id, label: `${p.name} (${p.client_name})` }));
  const openId = params.get("task");
  const openTask = (id) => setParams(id ? { task: id } : {});

  const move = async (id, status) => {
    const prev = data;
    const t = data.find((x) => x.id === id);
    if (!t || t.status === status) return;
    setData(data.map((x) => (x.id === id ? { ...x, status } : x)));
    try { await api.patch(`/tasks/${id}/move`, { status }); toast.success(`«${t.title}» → ${status}`); }
    catch (e) { setData(prev); toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Tareas" subtitle="Arrastre las tarjetas entre columnas para cambiar su estado."
        actions={<Button onClick={() => setCreating(true)} data-testid="new-task-button"><Plus className="mr-2 h-4 w-4" />Nueva tarea</Button>} />
      <FilterBar search={q} onSearch={setQ} values={f} onChange={setF} testid="tasks-filters" filters={[
        { key: "project_id", label: "Proyecto", options: projectOptions }, { key: "assignee_id", label: "Responsable", options: staff },
        { key: "priority", label: "Prioridad", options: optionList(PRIORITIES) }]}>
        <Tabs value={view} onValueChange={setView}><TabsList>
          {[["kanban", "Kanban"], ["lista", "Lista"], ["calendario", "Calendario"], ["cronograma", "Cronograma"]].map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`tasks-view-${k}`}>{l}</TabsTrigger>)}
        </TabsList></Tabs>
      </FilterBar>
      <ErrorBox text={error} />
      {loading && !data ? <Loading /> : (
        <>
          {view === "kanban" && <KanbanView tasks={data || []} onOpen={openTask} onMove={move} />}
          {view === "lista" && <ListView tasks={data || []} onOpen={openTask} name={name} />}
          {view === "calendario" && <CalendarView tasks={data || []} onOpen={openTask} />}
          {view === "cronograma" && <TimelineView tasks={data || []} onOpen={openTask} />}
        </>
      )}
      <TaskSheet taskId={openId} onClose={() => openTask(null)} onChanged={reload} projectOptions={projectOptions} />
      <FormDialog open={creating} onOpenChange={setCreating} title="Nueva tarea" testid="task-form" fields={taskFields(staff, projectOptions)}
        initial={{ priority: "Normal", status: "Pendiente", project_id: f.project_id || "" }}
        onSubmit={async (v) => { await api.post("/tasks", v); toast.success("Tarea creada"); reload(); }} />
    </div>
  );
}
